<script setup>
// Auto-extracted chrome for HeizungsbauBremen.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Heizungsbau Bremen" },
    brandTagline: { type: String, default: "Geprüft. Lokal. Bremen." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Heizungsbau Bremen");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Geprüft. Lokal. Bremen.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700;800;900&display=swap";
</script>

<template>
    <div class="hbb" :style="`--hbb-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="hbb-nav">
                    <div class="hbb-nav__inner">
                        <a class="hbb-logo" href="/" aria-label="Startseite Heizungsbau Bremen">
                            <span class="hbb-logo__crest" aria-hidden="true">HB</span>
                            <span class="hbb-logo__txt">
                                <span class="hbb-logo__name">{{ brandName }}</span>
                                <span class="hbb-logo__sub">{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="hbb-nav__links" aria-label="Sekundärnavigation">
                            <a href="#förderung">Förderung</a>
                            <a href="#verzeichnis">Verzeichnis</a>
                            <a href="#faq">Fragen</a>
                            <a href="#hbb-stepper" class="hbb-nav__cta">Anfrage starten</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="hbb-foot">
                    <div class="hbb-foot__inner">
                        <div class="hbb-foot__brand">
                            <span class="hbb-logo__crest" aria-hidden="true">HB</span>
                            <span>{{ brandName }} &middot; {{ brandTagline }}</span>
                        </div>
                        <nav class="hbb-foot__links" aria-label="Footer">
                            <a href="https://www.hwk-bremen.de" rel="nofollow noopener" target="_blank">HWK Bremen</a>
                            <a href="https://www.bab-bremen.de" rel="nofollow noopener" target="_blank">Bremer Aufbau-Bank</a>
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                        <p class="hbb-foot__copy">&copy; {{ new Date().getFullYear() }} heizungsbaubremen.de &middot; alle Angaben ohne Gewähr, vor Beauftragung HWK-Eintragung selbst prüfen.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

/* HBB v3.1 editorial - Linear/Stripe polish + Enpal/Thermondo conversion.
   hbb-* namespace preserved per AGENT.md. Mobile-first 375 base.
   Space Grotesk display + Inter body. Brand red = CTA red.
   Hairline borders only, no heavy shadows, asymmetric editorial layouts. */

.hbb {
    --hbb-ink: #14110d;
    --hbb-ink-2: #3a3530;
    --hbb-mute: #6f6862;
    --hbb-cream: #f7f4ee;
    --hbb-cream-2: #ede5d5;
    --hbb-surface: #fff;
    --hbb-line: rgba(20, 17, 13, 0.08);
    --hbb-line-strong: rgba(20, 17, 13, 0.16);
    --hbb-cta: #b91c1c;
    --hbb-cta-hover: #991b1b;
    --hbb-cta-glow: 0 8px 24px -8px rgba(185,28,28,0.5), 0 0 0 1px rgba(185,28,28,0.2);
    --hbb-ease: cubic-bezier(0.32, 0.72, 0, 1);

    background: var(--hbb-cream);
    color: var(--hbb-ink);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 17px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100dvh;
    padding-bottom: 88px;
    text-rendering: optimizeLegibility;
}
.hbb * { box-sizing: border-box; }
.hbb a { color: inherit; }
.hbb input, .hbb button, .hbb output { font-family: inherit; }
.hbb-num-tab { font-variant-numeric: tabular-nums; font-feature-settings: 'tnum'; }

/* ── Top nav ───────────────────────────────────────────────────── */
.hbb-nav {
    background: rgba(247, 244, 238, 0.85);
    backdrop-filter: saturate(160%) blur(12px);
    -webkit-backdrop-filter: saturate(160%) blur(12px);
    border-bottom: 1px solid var(--hbb-line);
    position: sticky;
    top: 0;
    z-index: 30;
}
.hbb-nav__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}
.hbb-logo { display: inline-flex; align-items: center; gap: 12px; text-decoration: none; color: var(--hbb-ink); }
.hbb-logo__crest {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: var(--hbb-ink);
    color: #fff;
    font-weight: 800;
    font-size: 11px;
    letter-spacing: 0.04em;
    border-left: 3px solid var(--hbb-cta);
    font-family: 'Space Grotesk', sans-serif;
}
.hbb-logo__txt { display: flex; flex-direction: column; line-height: 1.15; }
.hbb-logo__name { font-weight: 700; font-size: 15px; letter-spacing: -0.01em; }
.hbb-logo__sub { font-size: 11px; color: var(--hbb-mute); letter-spacing: 0.02em; }
.hbb-nav__links { display: none; align-items: center; gap: 24px; font-size: 14px; }
.hbb-nav__links a { text-decoration: none; color: var(--hbb-ink-2); transition: color 180ms var(--hbb-ease); }
.hbb-nav__links a:hover { color: var(--hbb-ink); }
.hbb-nav__cta {
    padding: 9px 16px;
    background: var(--hbb-ink);
    color: var(--hbb-cream) !important;
    border-radius: 999px;
    font-weight: 600;
}
.hbb-nav__cta:hover { background: var(--hbb-cta); color: #fff !important; }
@media (min-width: 720px) { .hbb-nav__links { display: inline-flex; } }

/* ── Hero ──────────────────────────────────────────────────────── */
.hbb-hero { padding: 28px 20px 8px; position: relative; }
.hbb-hero__inner {
    max-width: 1180px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 28px;
    align-items: stretch;
}
.hbb-hero__kicker {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    color: var(--hbb-ink-2);
    margin: 0 0 22px;
    padding: 8px 14px 8px 12px;
    background: var(--hbb-surface);
    border: 1px solid var(--hbb-line);
    border-radius: 999px;
    letter-spacing: 0.01em;
    width: max-content;
}
.hbb-hero__dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--hbb-cta);
    box-shadow: 0 0 0 4px rgba(185,28,28,0.14);
}
.hbb-hero__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(40px, 8.5vw, 80px);
    line-height: 1.0;
    letter-spacing: -0.035em;
    margin: 0 0 18px;
    color: var(--hbb-ink);
    text-wrap: balance;
}
.hbb-hero__em {
    font-style: normal;
    font-weight: 700;
    color: var(--hbb-cta);
    letter-spacing: -0.035em;
}
.hbb-hero__sub {
    font-size: clamp(16px, 2.2vw, 19px);
    line-height: 1.5;
    color: var(--hbb-ink-2);
    margin: 0 0 28px;
    max-width: 56ch;
}
.hbb-hero__ctaRow { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.hbb-hero__meta, .hbb-form__meta {
    font-size: 13px;
    font-weight: 500;
    color: var(--hbb-mute);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-variant-numeric: tabular-nums;
    margin: 0;
}
.hbb-form__meta { text-align: center; margin-top: 8px; }

/* Sample card */
.hbb-hero__sample { display: flex; align-items: flex-start; justify-content: center; }
.hbb-sample {
    background: var(--hbb-surface);
    border: 1px solid var(--hbb-line);
    border-radius: 20px;
    padding: 22px 22px 18px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 1px 0 rgba(255,255,255,0.5) inset, 0 24px 60px -32px rgba(20,17,13,0.18);
    position: relative;
}
.hbb-sample::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(185,28,28,0.18), rgba(185,28,28,0) 40%);
    pointer-events: none;
    mask: linear-gradient(#000, transparent 30%);
    -webkit-mask: linear-gradient(#000, transparent 30%);
}
.hbb-sample__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.hbb-sample__tag { font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--hbb-mute); font-weight: 600; }
.hbb-sample__dot { width: 9px; height: 9px; border-radius: 50%; background: var(--hbb-cta); box-shadow: 0 0 0 4px rgba(185,28,28,0.14); }
.hbb-sample__label { font-size: 12px; color: var(--hbb-mute); margin: 0 0 4px; }
.hbb-sample__big {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(40px, 8vw, 64px);
    letter-spacing: -0.045em;
    line-height: 1;
    margin: 0 0 18px;
    color: var(--hbb-ink);
    font-variant-numeric: tabular-nums;
}
.hbb-sample__bigPre { font-family: 'Inter', sans-serif; font-size: 14px; color: var(--hbb-mute); font-weight: 400; vertical-align: 0.55em; margin-right: 6px; letter-spacing: 0; }
.hbb-sample__bigCur { font-family: 'Inter', sans-serif; font-size: 22px; font-weight: 600; color: var(--hbb-cta); margin-left: 4px; vertical-align: 0.18em; }
.hbb-sample__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 13px;
    color: var(--hbb-ink-2);
    padding: 8px 0;
    border-top: 1px solid var(--hbb-line);
    margin: 0;
}
.hbb-sample__row span:last-child { font-weight: 700; color: var(--hbb-ink); }
.hbb-sample__row--strong span:last-child { color: var(--hbb-cta); }
.hbb-sample__foot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--hbb-line);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--hbb-mute);
}
.hbb-sample__foot svg { color: var(--hbb-cta); flex: none; }

/* Trust hairline strip */
.hbb-hero__photo { margin: 32px 0 0; }
.hbb-hero__photo img { width: 100%; height: auto; display: block; border: 1px solid var(--hbb-line); }
.hbb-hero__photo figcaption { font-size: 12px; color: var(--hbb-mute); margin-top: 8px; letter-spacing: 0.02em; }
.hbb-foerd__photo { margin: 28px 0 0; }
.hbb-foerd__photo img { width: 100%; height: auto; display: block; border: 1px solid var(--hbb-line); }
.hbb-foerd__photo figcaption { font-size: 12px; color: var(--hbb-mute); margin-top: 8px; letter-spacing: 0.02em; }
.hbb-trust { margin-top: 36px; border-top: 1px solid var(--hbb-line); border-bottom: 1px solid var(--hbb-line); }
.hbb-trust__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 18px 20px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}
.hbb-trust__item { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; font-size: 13px; }
.hbb-trust__k { color: var(--hbb-mute); letter-spacing: 0.12em; text-transform: uppercase; font-size: 11px; font-weight: 600; }
.hbb-trust__v { color: var(--hbb-ink); font-weight: 500; text-align: right; }

@media (min-width: 880px) {
    .hbb-hero { padding: 56px 32px 16px; }
    .hbb-hero__inner { grid-template-columns: 1.25fr 1fr; gap: 56px; }
    .hbb-hero__sample { justify-content: flex-end; align-items: center; }
    .hbb-sample { max-width: 440px; }
    .hbb-trust__inner { grid-template-columns: repeat(3, 1fr); gap: 28px; padding: 22px 32px; }
    .hbb-trust__item { flex-direction: column; align-items: flex-start; gap: 4px; }
    .hbb-trust__v { text-align: left; }
}

/* ── Stepper ───────────────────────────────────────────────────── */
.hbb-step { padding: 48px 20px 64px; }
.hbb-step__inner { max-width: 820px; margin: 0 auto; }

.hbb-step__head {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 28px;
}
.hbb-step__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.14em;
    color: var(--hbb-cta);
    margin: 0;
    padding: 6px 12px;
    background: rgba(185,28,28,0.08);
    border: 1px solid rgba(185,28,28,0.18);
    border-radius: 999px;
    width: max-content;
    font-variant-numeric: tabular-nums;
}
.hbb-step__titleWrap { min-width: 0; }
.hbb-step__kicker { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--hbb-mute); margin: 0 0 4px; font-weight: 600; }
.hbb-step__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(24px, 4vw, 32px);
    letter-spacing: -0.025em;
    margin: 0;
    color: var(--hbb-ink);
    line-height: 1.1;
}
.hbb-step__prog {
    height: 3px;
    background: var(--hbb-line);
    border-radius: 2px;
    overflow: hidden;
    margin-top: 8px;
}
.hbb-step__progFill { display: block; height: 100%; background: var(--hbb-cta); transition: width 320ms var(--hbb-ease); }

.hbb-chapter { display: flex; flex-direction: column; gap: 36px; }
.hbb-field { display: flex; flex-direction: column; gap: 14px; }
.hbb-field__lbl {
    display: flex;
    align-items: baseline;
    gap: 12px;
    font-size: 16px;
    font-weight: 600;
    color: var(--hbb-ink);
    letter-spacing: -0.005em;
}
.hbb-field__lbl em { font-weight: 400; font-style: italic; color: var(--hbb-mute); }
.hbb-field__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 13px;
    color: var(--hbb-mute);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    min-width: 22px;
}
.hbb-field__help { font-size: 13px; color: var(--hbb-mute); margin: 0; }

/* Radios */
.hbb-radios { display: grid; gap: 10px; }
@media (min-width: 600px) {
    .hbb-radios--2 { grid-template-columns: 1fr 1fr; }
    .hbb-radios--3 { grid-template-columns: 1fr 1fr 1fr; }
    .hbb-radios--4 { grid-template-columns: 1fr 1fr; }
    .hbb-radios--5 { grid-template-columns: 1fr 1fr 1fr; }
    .hbb-radios--6 { grid-template-columns: 1fr 1fr 1fr; }
    .hbb-radios--7 { grid-template-columns: 1fr 1fr 1fr; }
}
.hbb-radio {
    appearance: none;
    background: var(--hbb-surface);
    border: 1px solid var(--hbb-line);
    border-radius: 12px;
    padding: 14px 16px;
    text-align: left;
    cursor: pointer;
    color: var(--hbb-ink);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 60px;
    transition: border-color 180ms var(--hbb-ease), background 180ms var(--hbb-ease), transform 100ms var(--hbb-ease);
    font: inherit;
}
.hbb-radio:hover { border-color: var(--hbb-line-strong); background: var(--hbb-cream); }
.hbb-radio:active { transform: translateY(1px); }
.hbb-radio--on {
    border-color: var(--hbb-cta);
    background: var(--hbb-surface);
    box-shadow: 0 0 0 3px rgba(185,28,28,0.08);
}
.hbb-radio__bullet {
    width: 18px; height: 18px;
    border-radius: 50%;
    border: 1.5px solid var(--hbb-line-strong);
    display: inline-flex; align-items: center; justify-content: center;
    margin-top: 1px; flex: none;
    background: var(--hbb-surface);
    transition: border-color 180ms var(--hbb-ease);
}
.hbb-radio--on .hbb-radio__bullet { border-color: var(--hbb-cta); }
.hbb-radio__bulletDot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--hbb-cta);
    transform: scale(0);
    transition: transform 180ms var(--hbb-ease);
}
.hbb-radio--on .hbb-radio__bulletDot { transform: scale(1); }
.hbb-radio__body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.hbb-radio__main { font-weight: 600; font-size: 15px; line-height: 1.3; }
.hbb-radio__hint { font-size: 13px; color: var(--hbb-mute); line-height: 1.4; font-weight: 400; }

/* Chips */
.hbb-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.hbb-chip {
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    padding: 10px 14px;
    min-height: 44px;
    border: 1px solid var(--hbb-line);
    background: var(--hbb-surface);
    color: var(--hbb-ink-2);
    cursor: pointer;
    border-radius: 999px;
    transition: all 180ms var(--hbb-ease);
}
.hbb-chip:hover { border-color: var(--hbb-line-strong); color: var(--hbb-ink); }
.hbb-chip--on { background: var(--hbb-ink); color: var(--hbb-cream); border-color: var(--hbb-ink); }

/* Slider */
.hbb-slider {
    background: var(--hbb-surface);
    border: 1px solid var(--hbb-line);
    border-radius: 12px;
    padding: 18px 18px 14px;
}
.hbb-slider__row { display: flex; align-items: center; gap: 16px; }
.hbb-slider input[type=range] {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    height: 28px;
    min-height: 44px;
}
.hbb-slider input[type=range]::-webkit-slider-runnable-track {
    height: 4px; background: var(--hbb-line-strong); border-radius: 2px;
}
.hbb-slider input[type=range]::-moz-range-track {
    height: 4px; background: var(--hbb-line-strong); border-radius: 2px;
}
.hbb-slider input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--hbb-cta);
    border: 3px solid var(--hbb-surface);
    box-shadow: 0 1px 6px rgba(185,28,28,0.4);
    margin-top: -9px;
    cursor: grab;
}
.hbb-slider input[type=range]::-moz-range-thumb {
    width: 22px; height: 22px; border-radius: 50%;
    background: var(--hbb-cta);
    border: 3px solid var(--hbb-surface);
    box-shadow: 0 1px 6px rgba(185,28,28,0.4);
    cursor: grab;
}
.hbb-slider input[type=range]:focus { outline: none; }
.hbb-slider input[type=range]:focus::-webkit-slider-thumb { box-shadow: 0 0 0 4px rgba(185,28,28,0.18); }
.hbb-slider output {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--hbb-ink);
    min-width: 96px;
    text-align: right;
}
.hbb-slider__ticks {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    font-size: 11px;
    color: var(--hbb-mute);
    letter-spacing: 0.04em;
}

/* Text fields */
.hbb-textfield {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--hbb-surface);
    border: 1px solid var(--hbb-line);
    border-radius: 12px;
    padding: 4px 16px;
    transition: border-color 180ms var(--hbb-ease), background 180ms var(--hbb-ease);
}
.hbb-textfield:focus-within { border-color: var(--hbb-cta); box-shadow: 0 0 0 3px rgba(185,28,28,0.08); }
.hbb-textfield input {
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 18px;
    font-weight: 600;
    padding: 14px 0;
    color: var(--hbb-ink);
    min-height: 48px;
    font-variant-numeric: tabular-nums;
}
.hbb-textfield__unit { font-size: 13px; color: var(--hbb-mute); white-space: nowrap; }
.hbb-textfield--plz input { letter-spacing: 0.08em; }

.hbb-step__nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-top: 24px;
    margin-top: 8px;
    border-top: 1px solid var(--hbb-line);
}

/* ── Buttons ───────────────────────────────────────────────────── */
.hbb-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: inherit;
    font-weight: 600;
    font-size: 15px;
    letter-spacing: -0.005em;
    line-height: 1;
    padding: 14px 20px;
    min-height: 48px;
    border: 1px solid transparent;
    border-radius: 999px;
    cursor: pointer;
    transition: background 180ms var(--hbb-ease), color 180ms var(--hbb-ease), border-color 180ms var(--hbb-ease), transform 100ms var(--hbb-ease);
    text-decoration: none;
    white-space: nowrap;
}
.hbb-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.hbb-btn:active:not(:disabled) { transform: translateY(1px); }
.hbb-btn__arr { transition: transform 220ms var(--hbb-ease); }
.hbb-btn:not(:disabled):hover .hbb-btn__arr { transform: translateX(3px); }

.hbb-btn--primary {
    background: var(--hbb-cta);
    color: #fff;
    box-shadow: var(--hbb-cta-glow);
    font-weight: 700;
    letter-spacing: -0.012em;
    transition: background 180ms var(--hbb-ease), box-shadow 200ms var(--hbb-ease), transform 180ms var(--hbb-ease);
}
.hbb-btn--primary:hover:not(:disabled) {
    background: var(--hbb-cta-hover);
    box-shadow: 0 12px 32px -8px rgba(185,28,28,0.6), 0 0 0 1px rgba(185,28,28,0.25);
    transform: translateY(-1px);
}
.hbb-btn--ghost { background: transparent; color: var(--hbb-ink); border-color: var(--hbb-line-strong); }
.hbb-btn--ghost:hover:not(:disabled) { background: var(--hbb-surface); border-color: var(--hbb-ink); }
.hbb-btn--lg { padding: 22px 32px; font-size: 19px; min-height: 60px; font-weight: 800; letter-spacing: -0.01em; }
.hbb-btn--full { width: 100%; }
.hbb-btn--stick { padding: 12px 20px; min-height: 48px; font-size: 15px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
.hbb-btn--shout {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 900;
    font-size: 19px;
}
@media (min-width: 720px) {
    .hbb-btn--shout { font-size: 22px; letter-spacing: 0.09em; }
}

.hbb-back-link {
    background: transparent;
    border: 0;
    color: var(--hbb-mute);
    font-size: 14px;
    font-weight: 500;
    padding: 12px 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: color 180ms var(--hbb-ease);
    min-height: 44px;
}
.hbb-back-link:hover { color: var(--hbb-ink); }

/* ── Result / final ────────────────────────────────────────────── */
.hbb-result {
    background: var(--hbb-surface);
    border: 1px solid var(--hbb-line);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
}
.hbb-result::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: var(--hbb-cta);
}
.hbb-result__inner { padding: 36px 26px 28px; }

.hbb-money { padding-bottom: 24px; border-bottom: 1px solid var(--hbb-line); margin-bottom: 24px; }
.hbb-money__label { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--hbb-mute); margin: 0 0 14px; font-weight: 600; }
.hbb-money__value {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(58px, 12vw, 104px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    margin: 0 0 12px;
    color: var(--hbb-ink);
    font-variant-numeric: tabular-nums;
    text-wrap: balance;
}
.hbb-money__pre {
    font-family: 'Inter', sans-serif;
    font-size: clamp(16px, 2.4vw, 22px);
    color: var(--hbb-mute);
    font-weight: 400;
    letter-spacing: 0;
    margin-right: 10px;
    vertical-align: 0.55em;
}
.hbb-money__num { color: var(--hbb-cta); }
.hbb-money__cur {
    font-family: 'Inter', sans-serif;
    font-size: clamp(28px, 5vw, 44px);
    color: var(--hbb-cta);
    font-weight: 600;
    margin-left: 10px;
    vertical-align: 0.16em;
    letter-spacing: 0;
}
.hbb-money__hint { font-size: 14px; color: var(--hbb-ink-2); margin: 0; }

.hbb-pick { }
.hbb-pick__eyebrow { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--hbb-cta); margin: 0 0 12px; font-weight: 700; }
.hbb-pick__list { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
.hbb-pick__list li {
    display: grid;
    grid-template-columns: 32px 1fr;
    gap: 10px;
    align-items: center;
    background: var(--hbb-cream);
    border: 1px solid var(--hbb-line);
    border-radius: 10px;
    padding: 12px 14px;
}
.hbb-pick__no {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px; height: 28px;
    border-radius: 50%;
    background: var(--hbb-cta);
    color: #fff;
    font-weight: 800;
    font-size: 13px;
    font-family: 'Space Grotesk', sans-serif;
}
.hbb-pick__body { display: flex; flex-direction: column; }
.hbb-pick__name { font-weight: 700; font-size: 14px; }
.hbb-pick__place { font-size: 12px; color: var(--hbb-mute); }

/* Lead form */
.hbb-lead {
    padding: 32px 26px 28px;
    background: var(--hbb-cream);
    border-top: 1px solid var(--hbb-line);
}
.hbb-lead__head { margin-bottom: 24px; }
.hbb-lead__eyebrow { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--hbb-cta); margin: 0 0 6px; font-weight: 700; }
.hbb-lead__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(22px, 3.4vw, 28px);
    letter-spacing: -0.025em;
    margin: 0 0 8px;
    line-height: 1.15;
    color: var(--hbb-ink);
    text-wrap: balance;
}
.hbb-lead__sub { font-size: 15px; color: var(--hbb-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }

.hbb-form { display: flex; flex-direction: column; gap: 16px; }
.hbb-form__grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media (min-width: 560px) { .hbb-form__grid { grid-template-columns: 1fr 1fr; } }

.hbb-input { display: flex; flex-direction: column; gap: 6px; }
.hbb-input__lbl { font-size: 13px; font-weight: 600; color: var(--hbb-ink); }
.hbb-input__lbl em { font-weight: 400; font-style: italic; color: var(--hbb-mute); }
.hbb-input input {
    font-size: 16px;
    padding: 14px 16px;
    min-height: 50px;
    border: 1px solid var(--hbb-line-strong);
    border-radius: 10px;
    background: var(--hbb-surface);
    color: var(--hbb-ink);
    transition: border-color 180ms var(--hbb-ease), box-shadow 180ms var(--hbb-ease);
}
.hbb-input input:focus { outline: 0; border-color: var(--hbb-cta); box-shadow: 0 0 0 3px rgba(185,28,28,0.12); }
.hbb-input--err input { border-color: var(--hbb-cta); }
.hbb-input__err { font-size: 12px; color: var(--hbb-cta); margin: 4px 0 0; font-weight: 500; }
.hbb-input__err--center { text-align: center; margin-top: 8px; }

.hbb-consent {
    display: grid;
    grid-template-columns: 22px 1fr;
    gap: 12px;
    align-items: flex-start;
    font-size: 12px;
    color: var(--hbb-mute);
    line-height: 1.55;
    padding: 16px 0 4px;
    border-top: 1px solid var(--hbb-line);
    margin-top: 4px;
}
.hbb-consent input {
    width: 20px; height: 20px;
    margin-top: 2px;
    accent-color: var(--hbb-cta);
    cursor: pointer;
}
.hbb-consent__txt { display: block; }
.hbb-consent--err { color: var(--hbb-cta); }

.hbb-done {
    padding: 48px 28px;
    background: var(--hbb-cream);
    border-top: 1px solid var(--hbb-line);
    text-align: center;
    color: var(--hbb-ink);
}
.hbb-done svg { color: var(--hbb-cta); margin-bottom: 16px; }
.hbb-done h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 26px;
    letter-spacing: -0.025em;
    margin: 0 0 10px;
}
.hbb-done p { font-size: 15px; color: var(--hbb-ink-2); margin: 0 auto; max-width: 46ch; }
.hbb-done code { font-family: ui-monospace, monospace; background: var(--hbb-cream-2); padding: 2px 6px; font-size: 13px; border-radius: 4px; }

.hbb-result__back { padding: 0 26px 22px; }

/* ── Förderung editorial ───────────────────────────────────────── */
.hbb-foerd {
    padding: 72px 20px 56px;
    background: var(--hbb-surface);
    border-top: 1px solid var(--hbb-line);
    border-bottom: 1px solid var(--hbb-line);
}
.hbb-foerd__inner { max-width: 1080px; margin: 0 auto; }
.hbb-foerd__head { margin-bottom: 36px; max-width: 780px; }
.hbb-foerd__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--hbb-cta); margin: 0 0 12px; font-weight: 700; }
.hbb-foerd__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(28px, 5vw, 48px);
    letter-spacing: -0.03em;
    line-height: 1.05;
    margin: 0 0 14px;
    color: var(--hbb-ink);
    text-wrap: balance;
}
.hbb-foerd__h em { font-style: normal; font-weight: 700; color: var(--hbb-cta); }
.hbb-foerd__lead { font-size: 17px; color: var(--hbb-ink-2); margin: 0; line-height: 1.55; max-width: 56ch; }

.hbb-foerd__list { list-style: none; padding: 0; margin: 0; }
.hbb-foerd__row {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 20px;
    padding: 28px 0;
    border-top: 1px solid var(--hbb-line);
    align-items: start;
}
.hbb-foerd__row:last-child { border-bottom: 1px solid var(--hbb-line); }
.hbb-foerd__rowNum {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: var(--hbb-mute);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
    margin-top: 4px;
}
.hbb-foerd__row h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(20px, 2.6vw, 24px);
    letter-spacing: -0.02em;
    margin: 0 0 8px;
    line-height: 1.25;
}
.hbb-foerd__row p { font-size: 15px; color: var(--hbb-ink-2); margin: 0 0 6px; line-height: 1.55; max-width: 60ch; }
.hbb-foerd__row p strong { color: var(--hbb-cta); font-weight: 700; font-size: 19px; }
.hbb-foerd__cite { font-size: 12px; color: var(--hbb-mute); margin: 6px 0 0 !important; }
.hbb-foerd__cite :deep(a) { color: var(--hbb-ink-2); text-decoration: underline; }
.hbb-foerd__rowTag {
    display: none;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--hbb-mute);
    font-weight: 600;
    padding: 6px 12px;
    border: 1px solid var(--hbb-line);
    border-radius: 999px;
    background: var(--hbb-cream);
    align-self: start;
    margin-top: 6px;
    white-space: nowrap;
}
@media (min-width: 860px) {
    .hbb-foerd__row { grid-template-columns: 56px 1fr auto; gap: 32px; }
    .hbb-foerd__rowTag { display: inline-flex; align-items: center; }
}
.hbb-foerd__fineprint { font-size: 13px; color: var(--hbb-mute); margin: 24px 0 0; line-height: 1.55; max-width: 64ch; }

/* ── Directory ─────────────────────────────────────────────────── */
.hbb-list { padding: 72px 20px 56px; }
.hbb-list__inner { max-width: 1080px; margin: 0 auto; }
.hbb-list__head { margin-bottom: 32px; max-width: 780px; }
.hbb-list__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--hbb-cta); margin: 0 0 12px; font-weight: 700; }
.hbb-list__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(26px, 4vw, 36px);
    letter-spacing: -0.025em;
    line-height: 1.1;
    margin: 0 0 10px;
    text-wrap: balance;
}
.hbb-list__count { font-size: 14px; color: var(--hbb-mute); margin: 0; }
.hbb-list__count strong { color: var(--hbb-ink); font-weight: 700; }

.hbb-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
}
.hbb-card {
    background: var(--hbb-surface);
    border: 1px solid var(--hbb-line-strong);
    border-left: 4px solid var(--hbb-cta);
    border-radius: 6px;
    padding: 18px 18px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.hbb-card__head { border-bottom: 1px dashed var(--hbb-line); padding-bottom: 8px; }
.hbb-card__hwk { margin: 0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--hbb-mute); font-weight: 600; }
.hbb-card__name { margin: 4px 0 4px; font-family: 'Space Grotesk', system-ui, sans-serif; font-size: 17px; line-height: 1.2; letter-spacing: -0.01em; }
.hbb-card__owner { margin: 0; font-size: 13px; color: var(--hbb-ink-2); }
.hbb-card__addr { margin: 0; font-size: 14px; color: var(--hbb-ink); line-height: 1.45; }
.hbb-card__tags { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.hbb-card__tags li { font-size: 11px; padding: 3px 8px; border: 1px solid var(--hbb-line); border-radius: 999px; color: var(--hbb-ink-2); background: #fff; }
.hbb-card__contact { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.hbb-card__contact a { color: var(--hbb-cta); text-decoration: none; font-weight: 600; }
.hbb-card__contact a:hover { text-decoration: underline; }
.hbb-card__reg { margin: 4px 0 0; font-size: 11px; color: var(--hbb-mute); }
.hbb-card__reg strong { color: var(--hbb-ink); }

.hbb-placeholder {
    border: 1px solid var(--hbb-line-strong);
    background: var(--hbb-surface);
    border-radius: 6px;
    padding: 28px 26px;
    max-width: 780px;
}
.hbb-placeholder__lead { font-size: 16px; color: var(--hbb-ink-2); margin: 0 0 16px; }
.hbb-placeholder__lead strong { color: var(--hbb-ink); font-weight: 700; }
.hbb-placeholder__bullets { margin: 0 0 18px; padding-left: 22px; color: var(--hbb-ink-2); font-size: 15px; }
.hbb-placeholder__bullets li { margin: 6px 0; }
.hbb-placeholder__cta { margin: 0; }
.hbb-placeholder__link { color: var(--hbb-cta); font-weight: 700; text-decoration: underline; }
.hbb-placeholder__link:hover { color: var(--hbb-cta-hover); }
.hbb-pick__placeholder { font-size: 14px; color: var(--hbb-ink-2); margin: 8px 0 0; line-height: 1.5; }
.hbb-pick__link { color: var(--hbb-cta); font-weight: 700; text-decoration: underline; }

.hbb-entries { list-style: none; padding: 0; margin: 0; }
.hbb-entry {
    display: grid;
    grid-template-columns: 60px 1fr;
    gap: 16px;
    padding: 22px 0;
    border-top: 1px solid var(--hbb-line);
}
@media (min-width: 720px) { .hbb-entry { grid-template-columns: 80px 1.4fr 1fr; gap: 24px; align-items: start; } }
.hbb-entry:last-child { border-bottom: 1px solid var(--hbb-line); }
.hbb-entry--pick { background: rgba(185,28,28,0.03); }
.hbb-entry__rank { display: flex; flex-direction: column; gap: 4px; }
.hbb-entry__no {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 26px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--hbb-mute);
    letter-spacing: -0.02em;
}
.hbb-entry--pick .hbb-entry__no { color: var(--hbb-cta); }
.hbb-entry__badge { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--hbb-cta); font-weight: 700; }
.hbb-entry__name {
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    font-size: 19px;
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.015em;
}
.hbb-entry__place { font-size: 13px; color: var(--hbb-mute); margin: 4px 0 8px; }
.hbb-entry__rating { font-size: 13px; color: var(--hbb-ink-2); margin: 0 0 8px; }
.hbb-entry__tags { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 4px; }
.hbb-entry__tags li {
    font-size: 11px;
    padding: 3px 9px;
    background: var(--hbb-cream);
    border: 1px solid var(--hbb-line);
    border-radius: 999px;
    color: var(--hbb-ink-2);
    font-weight: 500;
}
.hbb-entry__meta { margin: 12px 0 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
@media (min-width: 720px) { .hbb-entry__meta { margin: 0; } }
.hbb-entry__meta div { display: flex; flex-direction: column; }
.hbb-entry__meta dt { font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--hbb-mute); }
.hbb-entry__meta dd { margin: 2px 0 0; font-size: 13px; color: var(--hbb-ink); font-variant-numeric: tabular-nums; font-weight: 500; }

/* ── FAQ ───────────────────────────────────────────────────────── */
.hbb-faq { padding: 72px 20px 88px; }
.hbb-faq__inner { max-width: 880px; margin: 0 auto; }
.hbb-faq__head { margin-bottom: 32px; }
.hbb-faq__kicker { font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--hbb-cta); margin: 0 0 12px; font-weight: 700; }
.hbb-faq__h {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: clamp(28px, 5vw, 44px);
    letter-spacing: -0.03em;
    line-height: 1.1;
    margin: 0;
    text-wrap: balance;
}
.hbb-faq__h em { font-style: normal; font-weight: 700; color: var(--hbb-cta); }
.hbb-faq__list { margin: 0; padding: 0; }
.hbb-faq__item { padding: 22px 0; border-top: 1px solid var(--hbb-line); }
.hbb-faq__item:last-child { border-bottom: 1px solid var(--hbb-line); }
.hbb-faq__item dt {
    display: flex;
    gap: 14px;
    font-size: 17px;
    font-weight: 600;
    color: var(--hbb-ink);
    margin-bottom: 8px;
    line-height: 1.35;
    letter-spacing: -0.01em;
}
.hbb-faq__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    color: var(--hbb-mute);
    min-width: 24px;
    margin-top: 3px;
    font-variant-numeric: tabular-nums;
}
.hbb-faq__item dd { margin: 0 0 0 38px; font-size: 15px; color: var(--hbb-ink-2); line-height: 1.55; max-width: 60ch; }

/* ── Footer ────────────────────────────────────────────────────── */
.hbb-foot { background: var(--hbb-ink); color: rgba(247,244,238,0.7); padding: 36px 20px 40px; }
.hbb-foot__inner { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 18px; }
.hbb-foot__brand { display: inline-flex; align-items: center; gap: 12px; color: var(--hbb-cream); font-size: 14px; font-weight: 600; }
.hbb-foot__links { display: flex; gap: 20px; font-size: 14px; flex-wrap: wrap; }
.hbb-foot__links a { color: rgba(247,244,238,0.7); text-decoration: none; transition: color 180ms var(--hbb-ease); }
.hbb-foot__links a:hover { color: var(--hbb-cream); }
.hbb-foot__copy { font-size: 12px; color: rgba(247,244,238,0.45); margin: 0; line-height: 1.55; max-width: 64ch; }
@media (min-width: 720px) {
    .hbb-foot__inner { grid-template-columns: auto 1fr auto; align-items: center; gap: 24px; }
    .hbb-foot__copy { grid-column: 1 / -1; padding-top: 18px; border-top: 1px solid rgba(247,244,238,0.1); }
}

/* ── Sticky CTA ────────────────────────────────────────────────── */
.hbb-stick {
    position: fixed;
    left: 12px; right: 12px;
    bottom: 12px;
    z-index: 40;
    background: var(--hbb-ink);
    color: var(--hbb-cream);
    border-radius: 14px;
    padding: 10px 12px 10px 18px;
    padding-bottom: max(10px, env(safe-area-inset-bottom, 10px));
    box-shadow: 0 18px 40px -16px rgba(20,17,13,0.5);
    border: 1px solid rgba(247,244,238,0.08);
}
.hbb-stick__inner { display: flex; align-items: center; justify-content: space-between; gap: 14px; max-width: 720px; margin: 0 auto; }
.hbb-stick__txt { display: flex; flex-direction: column; line-height: 1.2; font-size: 13px; min-width: 0; }
.hbb-stick__txt strong { font-size: 14px; font-weight: 700; color: var(--hbb-cream); }
.hbb-stick__txt span { font-size: 12px; color: rgba(247,244,238,0.6); }
.hbb-btn--stick { background: var(--hbb-cta); color: #fff; border-color: var(--hbb-cta); flex: none; }
.hbb-btn--stick:hover:not(:disabled) { background: var(--hbb-cta-hover); }
@media (min-width: 720px) { .hbb-stick { display: none; } }

/* ── Real photographic hero (HBB: red Bremen accent, hard-edged frame) ── */
.hbb-hero-photo {
    margin: 0;
    width: 100%;
    max-width: 460px;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
    border: 3px solid #B91C1C;
    box-shadow: 8px 8px 0 -1px rgba(185, 28, 28, 0.18), 0 8px 32px -12px rgba(20, 17, 13, 0.18);
    background: #fff;
}
.hbb-hero-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.hbb-hero-photo__caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: 10px 14px;
    background: rgba(185, 28, 28, 0.92);
    pointer-events: none;
}
.hbb-hero-photo__chip {
    color: #fff;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 700;
}

.hbb-cite {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 18px;
    justify-content: center;
}
.hbb-cite__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid rgba(185, 28, 28, 0.32);
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--hbb-ink, #1a1110);
    background: #fff;
}

.hbb-byline {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 18px auto 0;
    max-width: 460px;
    justify-content: center;
}
.hbb-byline__avatar {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background: #B91C1C;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    font-weight: 700;
    font-size: 13px;
}
.hbb-byline__txt { display: flex; flex-direction: column; line-height: 1.25; }
.hbb-byline__name { font-size: 13px; font-weight: 600; color: var(--hbb-ink, #1a1110); }
.hbb-byline__role { font-size: 12px; color: var(--hbb-mute, #6a5d5b); }

.hbb-entry__thumb {
    margin: 0;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid rgba(185, 28, 28, 0.22);
    background: #fff;
}
.hbb-entry__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

@media (prefers-reduced-motion: reduce) {
    .hbb *, .hbb *::before, .hbb *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}

</style>
